// SupportViewBanner — sticky red bar that fires when a platform admin
// is operating the app inside a `support_view` session (mig 435). The
// JWT claim is emitted by customize_access_token whenever the caller
// has an active support_views row. While the claim is present:
//
//   * is_platform_admin() short-circuits FALSE → no RLS bypass; the
//     platform admin sees the target tenant's data as that tenant's
//     admin would.
//   * This banner renders on every authenticated route, with the
//     target tenant name + a "Stop" button that calls end_support_view
//     and refreshes the session so the next JWT no longer carries the
//     claim.
//
// Visible everywhere: persistent, can't be dismissed. The point is to
// remind a SUPER_ADMIN that any write they make is on someone else's
// data. Audit triggers (mig 242) record everything regardless.

import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Eye, X, AlertTriangle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { refreshSessionAfterMetadataChange } from '@/lib/authRefresh'
import { useSupportView } from '@/lib/platformView'

interface ActiveSupportView {
  support_view_id:  string
  target_tenant_id: string
  ends_at:          string
  target_tenant?:   { display_name: string; slug: string } | null
}

// Initial state is FALSE; the useEffect below pulls the actual claim
// from the live session synchronously-ish on first render. A brief
// pre-paint flash without the banner is fine — better than blocking the
// app shell on session decoding every load.

export default function SupportViewBanner() {
  const qc = useQueryClient()
  // The shared claim reader (re-reads on auth changes, lock-free) — the same
  // one pages use to go read-only, so the bar and the page never disagree.
  const claimMaybe = useSupportView()
  const [now, setNow] = useState(() => Date.now())

  // Tick once a minute so the countdown stays roughly accurate without
  // re-rendering every second.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  // Look up the live session row so we can show target tenant + countdown.
  const { data: view } = useQuery({
    queryKey: ['support-view-active'],
    enabled:  claimMaybe,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_views')
        .select(`
          support_view_id, target_tenant_id, ends_at,
          target_tenant:tenants!support_views_target_tenant_id_fkey(display_name, slug)
        `)
        .is('ended_at', null)
        .gt('ends_at', new Date().toISOString())
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data as ActiveSupportView | null
    },
    staleTime: 30_000,
  })

  const stop = useMutation({
    // Pre-existing refresh-verify loop; this PR SIMPLIFIES it (routes through the
    // shared coordinator instead of supabase.auth.refreshSession), but the loop
    // keeps it over fallow's threshold and the new-only gate flags any edit to
    // it. Suppress.
    // fallow-ignore-next-line complexity
    mutationFn: async () => {
      const { error } = await supabase.rpc('end_support_view')
      if (error) throw error
      // Force a new JWT so the support_view claim falls away, then verify it
      // actually cleared before reloading. Refresh through the shared single-
      // flight coordinator (authRefresh.ts), NOT supabase.auth.refreshSession():
      // the old loop rotated the one-time refresh_token up to SIX times and,
      // interleaved with the watchdog / tenant switcher / auth-js, replayed a
      // consumed token and revoked live sessions (2026-06-15). Each iteration is
      // single-flight + rotation-guarded; we still loop because an adopted
      // concurrent refresh may predate the end_support_view write.
      for (let attempt = 0; attempt < 4; attempt++) {
        const r = await refreshSessionAfterMetadataChange()
        if (r.ok) {
          try {
            const payload = JSON.parse(atob(r.session.access_token.split('.')[1]))
            if (!payload?.support_view) break
          } catch { /* malformed — retry */ }
        } else if (r.reason === 'revoked') {
          break   // session gone — the reload lands on /login
        }
        await new Promise(res => setTimeout(res, 200 + attempt * 100))
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-view-active'] })
      // Bounce the user back to login (cleanest reset) or just reload.
      window.location.reload()
    },
  })

  if (!claimMaybe) return null

  const minutesLeft = view?.ends_at
    ? Math.max(0, Math.round((new Date(view.ends_at).getTime() - now) / 60_000))
    : null
  const targetName = view?.target_tenant?.display_name ?? '…'

  return (
    <div
      role="alert"
      className="bg-red-700 text-red-50 light:text-on-fill border-b-2 border-red-900 px-3 py-2 flex items-center gap-2 text-sm shadow-md print:hidden">
      <Eye size={16} className="shrink-0" />
      <span className="font-semibold truncate">
        Support view — viewing <span className="underline">{targetName}</span>
      </span>
      <span className="text-red-200 light:text-on-fill/80 text-xs hidden sm:inline">
        (writes are logged; please diagnose only)
      </span>
      {minutesLeft != null && (
        <span className="text-red-200 light:text-on-fill/80 text-xs ml-auto sm:ml-3 shrink-0">
          {minutesLeft > 0 ? `${minutesLeft} min left` : 'expiring'}
        </span>
      )}
      <button
        onClick={() => stop.mutate()}
        disabled={stop.isPending}
        className="tap-target-y ml-auto sm:ml-3 shrink-0 bg-red-900 hover:bg-red-950 rounded px-2 py-1 text-xs font-semibold flex items-center gap-1 disabled:opacity-50">
        <X size={12} /> {stop.isPending ? 'Stopping…' : 'Stop'}
      </button>
      {stop.error && (
        <span className="text-red-200 text-xs flex items-center gap-1 ml-2">
          <AlertTriangle size={12} /> {(stop.error as Error).message}
        </span>
      )}
    </div>
  )
}
